import { SystemConfiguration } from "@nt-portal/server-sdk";
import { store } from "../../redux/store";
import { EnvVariable } from "../../types";
export type AppSettingType = "env" | "configuration" | "function"
export type AppSettingValue = string | number | boolean | string[] | null | undefined;

export interface BaseSettings {
    [key: string]: AppSettingValue;
}


export interface FabrixApiPaths extends BaseSettings {
    // dataTamerServer: string;
    // dataTamerSocket: string;
}

export interface AppSettingDefinition {
    defaultValue: AppSettingValue;
    type: AppSettingType;
    key?: string;
    func?: (settings: AppSettings) => AppSettingValue;
    format?: (value: string) => AppSettingValue
}


export interface AppSettings extends BaseSettings {
    jwtClientId: string;
    production: boolean;
    systemEnvironment?: string;
    fabrixServer: string;
    fabrixSockets: string;
    tcmServer: string;
    tcmSockets: string;
    ivtServer: string;
    ivtSockets: string;
    eTenderUrl: string;
    eTenderEnabled: boolean;
    docChatServer: string;
    docChatSockets: string;
    aiPersona: string
    metaDataDescription: string;
    metaDataTitle: string;
    metaDataImage: string;
    graphDataServer: string;
    graphDataSockets: string;
    vcServer: string;
    sessionTimeout: number;
    loginModes: string[];
    theme: string;
}

export interface IAppSettings {
    production: AppSettingDefinition;
    systemEnvironment: AppSettingDefinition;
    fabrixServer: AppSettingDefinition;
    fabrixSockets: AppSettingDefinition;
    docChatServer: AppSettingDefinition;
    docChatSockets: AppSettingDefinition;
    tcmServer: AppSettingDefinition;
    tcmSockets: AppSettingDefinition;
    ivtServer: AppSettingDefinition;
    ivtSockets: AppSettingDefinition;
    aiPersona: AppSettingDefinition;
    metaDataDescription: AppSettingDefinition;
    metaDataTitle: AppSettingDefinition;
    metaDataImage: AppSettingDefinition;
    graphDataServer: AppSettingDefinition;
    graphDataSockets: AppSettingDefinition;
    vcServer: AppSettingDefinition;
    sessionTimeout: AppSettingDefinition;
    loginModes: AppSettingDefinition;
    eTenderUrl: AppSettingDefinition;
    eTenderEnabled: AppSettingDefinition;
    theme: AppSettingDefinition;
}

const getSocketValue = (server: string): AppSettingValue => {
    if (server.startsWith("http")) {
        let url = new URL(server);
        let port = parseInt(url.port, 10) + 1;
        url.port = port.toString();
        url.pathname = "";
        return url.toString();
    }
    return "/"
}

const getTCMSocketValue = (setting: AppSettings): AppSettingValue => {

    return getSocketValue(setting.tcmServer as string);



}

const getIvtSocketValue = (setting: AppSettings): AppSettingValue => {

    return getSocketValue(setting.ivtServer as string);



}

const getDocSocketValue = (setting: AppSettings): AppSettingValue => {
    return getSocketValue(setting.docChatServer as string);
}

const getFbxSocketValue = (setting: AppSettings): AppSettingValue => {
    return getSocketValue(setting.fabrixServer as string);
}

const getGraphSocketValue = (setting: AppSettings): AppSettingValue => {
    return getSocketValue(setting.graphDataServer as string);
}

export const appSettingsDefinition = () => {
    let settings: IAppSettings = {

        production: {
            defaultValue: "production",
            key: "NODE_ENV",
            type: "env",
            format: (value) => {
                let isDev = process.env.REACT_APP_DEV_MODE?.trim() === "true";
                return value === 'production' && !isDev
            }
        },
        systemEnvironment: {
            defaultValue: "",
            key: "SYSTEM_ENVIRONMENT",
            type: "env"
        },
        fabrixServer: {
            defaultValue: "http://localhost:4405",
            key: "FABRIX_SERVER",
            type: "env",
        },
        tcmServer: {
            defaultValue: "http://localhost:4510",
            key: "TCM_SERVER",
            type: "env",
        },
        ivtServer: {
            defaultValue: "http://localhost:4525",
            key: "IVT_SERVER",
            type: "env",
        },
        docChatServer: {
            defaultValue: "http://localhost:4515",
            key: "DOC_CHAT_SERVER",
            type: "env",
        },
        docChatSockets: {
            type: "function",
            defaultValue: "http://localhost:4516",
            func: getDocSocketValue
        },
        tcmSockets: {
            type: "function",
            defaultValue: "http://localhost:4511",
            func: getTCMSocketValue
        },
        ivtSockets: {
            type: "function",
            defaultValue: "http://localhost:4526",
            func: getTCMSocketValue
        },
        fabrixSockets: {
            type: "function",
            defaultValue: "http://localhost:4406",
            func: getFbxSocketValue
        },
        graphDataServer: {
            type: "env",
            defaultValue: "http://localhost:4205",
            key: "GB_SERVER"
        },
        graphDataSockets: {
            type: "function",
            defaultValue: "http://localhost:4206",
            func: getGraphSocketValue
        },
        vcServer: {
            defaultValue: "",
            key: "VC_SERVER",
            type: "env",
        },
        aiPersona: {
            defaultValue: "",
            key: "ai-persona",
            type: "configuration"
        },
        eTenderEnabled: {
            defaultValue: false,
            key: "e-tender-enabled",
            type: "configuration"
        },
        eTenderUrl: {
            defaultValue: "https://admin-uat.etenders.gov.za",
            key: "e-tender-url",
            type: "configuration"
        },
        metaDataDescription: {
            defaultValue: "",
            key: "meta-data-description",
            type: "configuration"
        },
        metaDataImage: {
            defaultValue: "",
            key: "meta-data-image",
            type: "configuration"
        },
        metaDataTitle: {
            defaultValue: "",
            key: "meta-data-title",
            type: "configuration"
        },
        sessionTimeout: {
            defaultValue: 15,
            key: "tokenLifeSpan",
            type: "configuration"
        },
        theme: {
            defaultValue: "default-theme",
            key: "theme",
            type: "configuration"
        },
        loginModes: {
            defaultValue: ["office", "user", "ory"],
            key: "login-modes",
            type: "configuration"
        }
    }
    return settings;
}




function getConfigurationDataValue(data: SystemConfiguration[], settingName: string, defaultValue: AppSettingValue): AppSettingValue {

    const parseBoolean = (value?: string | boolean): boolean => {
        if (value === undefined || value === null) return false;
        if (typeof value === "boolean") return value;
        return value.toLowerCase() === 'true' || value.toLowerCase() === 't';
    }

    if (data) {
        let item = data.find(x => x.name.toLowerCase() === settingName.toLowerCase());
        if (item) {
            switch (item.dataType) {
                case "string": return item.value;
                case "integer": return parseInt(item.value);
                case "float": return parseFloat(item.value);
                case "boolean": return parseBoolean(item.value);
                case "string_array": {
                    if (Array.isArray(item.value))
                        return item.value;

                    return item.value.split(",").map((x: string) => x.trim())

                }
                default: return item.value;
            }
        }
    }
    return defaultValue;
}


export const buildSettings = (settings: AppSettings, vars: EnvVariable[], config: SystemConfiguration[]) => {
    let settingsDef = appSettingsDefinition();

    const getEnvValue = (name: string) => {

        let values = vars;
        if (values) {
            let item = values.find(x => x.name === name);
            if (item && item.value)
                return item.value;
        }
        return ''
    }


    let result = { ...settings }

    for (const [key, value] of Object.entries(settingsDef)) {
        let def = value as AppSettingDefinition;
        if (def.type === "env" && vars.length > 0 && def.key) {

            let envValue = getEnvValue(def.key) as any;

            if (envValue == undefined || envValue === null || envValue === "")
                envValue = def.defaultValue;

            
            if (def.format) {
                result[key] = def.format(envValue)
            } else {
                result[key] = envValue;
            }

        } else if (def.type === "configuration" && config.length > 0 && def.key) {
            result[key] = getConfigurationDataValue(config, def.key, def.defaultValue);
        }
    }


    let devMode = process.env.REACT_APP_DEV_MODE?.trim() === "true"
    if (devMode) {
        let fabrixServer = process.env.REACT_APP_FABRIX_SERVER?.trim()
        if (fabrixServer)
            result.fabrixServer = `${fabrixServer}/api`;
        let tcmServer = process.env.REACT_APP_TCM_SERVER?.trim()
        if (tcmServer)
            result.tcmServer = tcmServer;
        let vcServer = process.env.REACT_APP_VC_SERVER?.trim()
        if (vcServer)
            result.vcServer = vcServer;

    } else {
        result.fabrixServer = "/api";
        result.fabrixSockets = "/";
        result.tcmServer = '/api/tcm';
        result.tcmSockets = '/';
        result.ivtServer = '/api/ivt';
        result.ivtSockets = '/';
        result.graphDataServer = '/api/gds';
        result.graphDataSockets = '/';
        result.docChatServer = '/api/das';
        result.docChatSockets = '/';
        result.vcServer = '/vcs';
    }


    //We need to process all the other settings first then the function ones.
    for (const [key, value] of Object.entries(settingsDef)) {
        let def = value as AppSettingDefinition;
        if (def?.type === "function" && def.func)
            result[key] = def.func(result);
    }

    return result;

}




export const getAppSettings = () => {
    return store.getState().settings.appSettings
}


